import {
  HomeIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
  UserIcon,
  CreditCardIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  DocumentChartBarIcon,
  ChatBubbleBottomCenterTextIcon,
  AcademicCapIcon,
  TruckIcon,
  ClipboardDocumentCheckIcon,
  MapPinIcon,
  MapIcon,
  SignalIcon,
  GlobeAltIcon,
  ServerIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  ComputerDesktopIcon,
  WifiIcon,
  CubeIcon,
  BellAlertIcon,
  ChartPieIcon,
  CircleStackIcon,
  MegaphoneIcon,
  BellIcon,
  CommandLineIcon,
  BookOpenIcon,
  UserPlusIcon,
  PhoneIcon,
  EnvelopeIcon,
  StarIcon,
  PlayIcon,
  ClipboardDocumentListIcon,
  IdentificationIcon,
  CakeIcon,
  NewspaperIcon,
  PhotoIcon,
  CheckCircleIcon,
  FolderIcon,
  QrCodeIcon,
  BeakerIcon,
  SparklesIcon,
  PresentationChartLineIcon,
  Squares2X2Icon,
  HandRaisedIcon,
  LifebuoyIcon,
  QuestionMarkCircleIcon,
  ClipboardIcon,
  TagIcon,
  BanknotesIcon,
  ArrowPathIcon,
  BookmarkIcon,
  WalletIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';

export interface NavItem {
  name: string;
  href: string;
  icon: any;
  current: boolean;
  badge?: string;
  hasDropdown?: boolean;
  subItems?: NavSubItem[];
}

export interface NavSubItem {
  name: string;
  href: string;
  icon: any;
  current: boolean;
  badge?: string;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

export type UserRole = 'super_admin' | 'school_admin' | 'teacher' | 'parent' | 'driver' | 'student';

// Super Admin Navigation (Platform-wide management) - Original extensive version
export const getSuperAdminNavigation = (): NavSection[] => [
  {
    title: 'Platform Management',
    items: [
      { name: 'Dashboard', href: '/admin/dashboard', icon: HomeIcon, current: false },
      { name: 'Institutions', href: '/admin/institutions', icon: BuildingOfficeIcon, current: false },
      { name: 'Users', href: '/admin/users', icon: UsersIcon, current: false },
      { name: 'Groups', href: '/admin/groups', icon: UserGroupIcon, current: false },
      { name: 'School Admins', href: '/admin/school-admins', icon: ShieldCheckIcon, current: false },
      { name: 'Teachers', href: '/admin/teachers', icon: AcademicCapIcon, current: false },
      { name: 'Students', href: '/admin/students', icon: UserIcon, current: false },
      { name: 'Platform Analytics', href: '/admin/analytics', icon: ChartPieIcon, current: false },
      { name: 'Invoices', href: '/admin/invoices', icon: DocumentTextIcon, current: false },
      { name: 'Billing & Subscriptions', href: '/admin/billing', icon: CreditCardIcon, current: false },
      // { name: 'My Profile', href: '/admin/profile', icon: UserCircleIcon, current: false },
    ]
  },
  {
    title: 'Academic Operations',
    items: [
      { name: 'Grades', href: '/admin/academic/grades', icon: CircleStackIcon, current: false },
      { name: 'Streams', href: '/admin/academic/streams', icon: Squares2X2Icon, current: false },
      { name: 'Lessons', href: '/admin/academic/lessons', icon: BookOpenIcon, current: false },
      // { name: 'Classes', href: '/admin/academic/classes', icon: DocumentTextIcon, current: false },
      { name: 'Sessions', href: '/admin/academic/sessions', icon: PlayIcon, current: false },
      { name: 'Attendance Tracking', href: '/admin/academic/attendance', icon: ClipboardDocumentCheckIcon, current: false },
      // { name: 'Timetables', href: '/admin/academic/timetables', icon: CalendarDaysIcon, current: false },
      // { name: 'Academic Calendar', href: '/admin/academic/calendar', icon: CalendarDaysIcon, current: false },
      // { name: 'Subject Management', href: '/admin/academic/subjects', icon: AcademicCapIcon, current: false },
      // { name: 'Performance Reports', href: '/admin/academic/reports', icon: DocumentChartBarIcon, current: false },
    ]
  },
  {
    title: 'RFID & Hardware',
    items: [
      { name: 'RFID Terminals', href: '/admin/rfid/terminals', icon: ComputerDesktopIcon, current: false },
      { name: 'RFID Cards', href: '/admin/rfid/cards', icon: CreditCardIcon, current: false },
      // { name: 'Card Readers', href: '/admin/rfid/readers', icon: DeviceTabletIcon, current: false },
      // { name: 'POS Terminals', href: '/admin/rfid/pos-terminals', icon: CurrencyDollarIcon, current: false },
      { name: 'Network Devices', href: '/admin/rfid/network', icon: WifiIcon, current: false },
      { name: 'Hardware Status', href: '/admin/rfid/status', icon: CubeIcon, current: false },
      { name: 'Firmware Management', href: '/admin/rfid/firmware', icon: CommandLineIcon, current: false },
      { name: 'Device Configuration', href: '/admin/rfid/config', icon: Cog6ToothIcon, current: false },
      { name: 'Terminal Health', href: '/admin/rfid/health', icon: SignalIcon, current: false },
    ]
  },
  {
    title: 'Transport & Safety',
    items: [
      { name: 'Fleet Management', href: '/admin/transport/fleet', icon: TruckIcon, current: false },
      { name: 'Route Planning', href: '/admin/transport/routes', icon: MapPinIcon, current: false },
      { name: 'Trips', href: '/admin/transport/trips', icon: CalendarDaysIcon, current: false },
      { name: 'Live Tracking', href: '/admin/transport/tracking', icon: SignalIcon, current: false },
      { name: 'Safety Protocols', href: '/admin/transport/safety', icon: ShieldCheckIcon, current: false },
      { name: 'Emergency Alerts', href: '/admin/transport/alerts', icon: BellAlertIcon, current: false },
      { name: 'Driver Management', href: '/admin/transport/drivers', icon: UserIcon, current: false },
      { name: 'Vehicle Maintenance', href: '/admin/transport/maintenance', icon: WrenchScrewdriverIcon, current: false },
    ]
  },
  {
    title: 'System Administration',
    items: [
      { name: 'System Health', href: '/admin/system/health', icon: ServerIcon, current: false },
      { name: 'Performance Monitor', href: '/admin/system/performance', icon: ChartBarIcon, current: false },
      { name: 'Database Admin', href: '/admin/system/database', icon: CircleStackIcon, current: false },
      { name: 'API Management', href: '/admin/system/api', icon: GlobeAltIcon, current: false },
      { name: 'Security Center', href: '/admin/system/security', icon: ShieldCheckIcon, current: false },
      { name: 'Backup & Recovery', href: '/admin/system/backup', icon: CircleStackIcon, current: false },
      { name: 'System Configuration', href: '/admin/system/config', icon: Cog6ToothIcon, current: false },
      { name: 'Audit Logs', href: '/admin/system/logs', icon: DocumentTextIcon, current: false },
    ]
  },
  {
    title: 'Communication Hub',
    items: [
      { name: 'Announcements', href: '/admin/communication/announcements', icon: MegaphoneIcon, current: false },
      { name: 'Events Calendar', href: '/admin/communication/events', icon: CalendarDaysIcon, current: false },
      { name: 'Notifications', href: '/admin/communication/notifications', icon: BellIcon, current: false },
    ]
  }
];

// School Admin Navigation (School-specific management)
export const getSchoolAdminNavigation = (): NavSection[] => [
  {
    title: '',
    items: [
      { name: 'Dashboard', href: '/schoolAdmin/dashboard', icon: HomeIcon, current: false },
    ]
  },
  {
    title: 'User Management',
    items: [
      { name: 'Teachers', href: '/schoolAdmin/teachers', icon: AcademicCapIcon, current: false },
      { name: 'Students', href: '/schoolAdmin/students', icon: UserIcon, current: false },
      { name: 'Parents', href: '/schoolAdmin/parents', icon: UserGroupIcon, current: false },
      { name: 'Drivers', href: '/schoolAdmin/drivers', icon: TruckIcon, current: false },
      // { name: 'My Profile', href: '/schoolAdmin/profile', icon: UserCircleIcon, current: false },
    ]
  },
  {
    title: 'Academic Management',
    items: [
      { name: 'Grades', href: '/schoolAdmin/grades', icon: CircleStackIcon, current: false },
      { name: 'Streams', href: '/schoolAdmin/streams', icon: Squares2X2Icon, current: false },
      { name: 'Lessons', href: '/schoolAdmin/lessons', icon: BookOpenIcon, current: false },
      { name: 'Sessions', href: '/schoolAdmin/sessions', icon: PlayIcon, current: false },
      { name: 'Attendance Tracking', href: '/schoolAdmin/academic/attendance', icon: ClipboardDocumentCheckIcon, current: false },
      // { name: 'Classes', href: '/schoolAdmin/classes', icon: DocumentTextIcon, current: false, badge: '35' },
      // { name: 'Subjects', href: '/schoolAdmin/subjects', icon: AcademicCapIcon, current: false, badge: '18' },
      // { name: 'Attendance', href: '/schoolAdmin/attendance', icon: ClipboardDocumentCheckIcon, current: false },
      // { name: 'Timetables', href: '/schoolAdmin/timetables', icon: CalendarDaysIcon, current: false },
      // { name: 'Academic Calendar', href: '/schoolAdmin/calendar', icon: CalendarDaysIcon, current: false },
      // { name: 'Grade Reports', href: '/schoolAdmin/grade-reports', icon: DocumentTextIcon, current: false },
      // { name: 'Exam Management', href: '/schoolAdmin/exams', icon: ClipboardDocumentListIcon, current: false },
    ]
  },
  {
    title: 'Library Management',
    items: [
      { name: 'Library Dashboard', href: '/schoolAdmin/library/dashboard', icon: BookOpenIcon, current: false },
      { name: 'Books', href: '/schoolAdmin/library/books', icon: BookmarkIcon, current: false },
      { name: 'Borrowings', href: '/schoolAdmin/library/borrowings', icon: ArrowPathIcon, current: false },
      { name: 'Fines', href: '/schoolAdmin/library/fines', icon: BanknotesIcon, current: false },
      { name: 'Categories', href: '/schoolAdmin/library/categories', icon: TagIcon, current: false },
    ]
  },
  {
    title: 'Meals & Cafeteria',
    items: [
      { name: 'Meal Types', href: '/schoolAdmin/meals/types', icon: CakeIcon, current: false },
      { name: 'Meal Sessions', href: '/schoolAdmin/meals/sessions', icon: PlayIcon, current: false },
      { name: 'Meal Attendance', href: '/schoolAdmin/meals/attendance', icon: ClipboardDocumentCheckIcon, current: false },
    ]
  },
  {
    title: 'Wallet & Finance',
    items: [
      { name: 'Wallet Dashboard', href: '/schoolAdmin/wallet', icon: WalletIcon, current: false },
      { name: 'Generate Fees', href: '/schoolAdmin/fee-generation', icon: SparklesIcon, current: false },
      { name: 'Invoices', href: '/schoolAdmin/invoices', icon: DocumentTextIcon, current: false },
      { name: 'Billing Setup', href: '/schoolAdmin/billing', icon: BanknotesIcon, current: false },
      { name: 'Transactions', href: '/schoolAdmin/wallet/transactions', icon: ArrowPathIcon, current: false },
      { name: 'Credit Wallet', href: '/schoolAdmin/wallet/credit', icon: ArrowUpIcon, current: false },
      { name: 'Debit Wallet', href: '/schoolAdmin/wallet/debit', icon: ArrowDownIcon, current: false },
      { name: 'Payment Config', href: '/schoolAdmin/payment-config', icon: CreditCardIcon, current: false },
    ]
  },
  {
    title: 'RFID & Technology',
    items: [
      { name: 'RFID Terminals', href: '/schoolAdmin/rfid-terminals', icon: ComputerDesktopIcon, current: false },
      { name: 'RFID Cards', href: '/schoolAdmin/rfid-cards', icon: CreditCardIcon, current: false },
      // { name: 'Card Readers', href: '/schoolAdmin/card-readers', icon: DeviceTabletIcon, current: false },
      // { name: 'POS Terminals', href: '/schoolAdmin/pos-terminals', icon: CurrencyDollarIcon, current: false },
      // { name: 'Hardware Status', href: '/schoolAdmin/hardware-status', icon: CubeIcon, current: false },
      // { name: 'Network Health', href: '/schoolAdmin/network', icon: WifiIcon, current: false },
      // { name: 'Device Configuration', href: '/schoolAdmin/device-config', icon: Cog6ToothIcon, current: false },
    ]
  },
  {
    title: 'Transport & Safety',
    items: [
      { name: 'Fleet Overview', href: '/schoolAdmin/fleet', icon: TruckIcon, current: false },
      { name: 'Route Planning', href: '/schoolAdmin/transport/routes', icon: MapPinIcon, current: false },
      { name: 'Trips', href: '/schoolAdmin/trips', icon: CalendarDaysIcon, current: false },
      { name: 'Live Tracking', href: '/schoolAdmin/live-tracking', icon: SignalIcon, current: false },
      { name: 'Driver Management', href: '/schoolAdmin/driver-management', icon: UserIcon, current: false },
    ]
  },
  {
    title: 'GPS Fleet Management',
    items: [
      { name: 'GPS Dashboard', href: '/schoolAdmin/gps/dashboard', icon: MapPinIcon, current: false },
      { name: 'Fleet Tracking', href: '/schoolAdmin/gps/fleet', icon: TruckIcon, current: false },
      { name: 'Geofence Management', href: '/schoolAdmin/gps/geofences', icon: MapIcon, current: false },
      { name: 'Alarm Monitoring', href: '/schoolAdmin/gps/alarms', icon: ExclamationTriangleIcon, current: false },
      { name: 'Vehicle Controls', href: '/schoolAdmin/gps/controls', icon: CommandLineIcon, current: false },
      { name: 'GPS Reports', href: '/schoolAdmin/gps/reports', icon: DocumentChartBarIcon, current: false },
      { name: 'GPS Settings', href: '/schoolAdmin/gps/settings', icon: Cog6ToothIcon, current: false },
    ]
  },
  {
    title: 'Communication',
    items: [
      { name: 'Announcements', href: '/schoolAdmin/communication/announcements', icon: MegaphoneIcon, current: false },
      { name: 'Events Calendar', href: '/schoolAdmin/communication/events', icon: CalendarDaysIcon, current: false },
      { name: 'Notifications', href: '/schoolAdmin/communication/notifications', icon: BellIcon, current: false },
    ]
  },
  // {
  //   title: 'Finance & Administration',
  //   items: [
  //     { name: 'Fee Management', href: '/schoolAdmin/fees', icon: CurrencyDollarIcon, current: false },
  //     { name: 'Payment Tracking', href: '/schoolAdmin/payments', icon: CreditCardIcon, current: false },
  //     { name: 'Financial Reports', href: '/schoolAdmin/financial-reports', icon: DocumentChartBarIcon, current: false },
  //     { name: 'Budget Planning', href: '/schoolAdmin/budget', icon: ChartPieIcon, current: false },
  //   ]
  // },
  {
    title: 'Reports & Analytics',
    items: [
      { name: 'School Analytics', href: '/schoolAdmin/analytics', icon: ChartBarIcon, current: false },
      { name: 'Attendance Reports', href: '/schoolAdmin/attendance-reports', icon: PresentationChartLineIcon, current: false },
      { name: 'Performance Reports', href: '/schoolAdmin/performance-reports', icon: DocumentChartBarIcon, current: false },
      { name: 'Transport Reports', href: '/schoolAdmin/transport-reports', icon: TruckIcon, current: false },
      { name: 'Meal Reports', href: '/schoolAdmin/meals/reports', icon: CakeIcon, current: false },
      // { name: 'Safety Reports', href: '/schoolAdmin/safety-reports', icon: ShieldCheckIcon, current: false },
    ]
  },
  // {
  //   title: 'School Services',
  //   items: [
  //     { name: 'Library Management', href: '/schoolAdmin/library', icon: BookOpenIcon, current: false },
  //     { name: 'Cafeteria & Meals', href: '/schoolAdmin/cafeteria', icon: CakeIcon, current: false },
  //     { name: 'Health Center', href: '/schoolAdmin/health', icon: HeartIcon, current: false },
  //     { name: 'Events & Activities', href: '/schoolAdmin/events', icon: CalendarDaysIcon, current: false, badge: '3' },
  //     { name: 'Photo Gallery', href: '/schoolAdmin/gallery', icon: PhotoIcon, current: false },
  //   ]
  // },
  {
    title: 'Settings & Configuration',
    items: [
      { name: 'School Profile', href: '/schoolAdmin/school-profile', icon: BuildingOfficeIcon, current: false },
      { name: 'System Settings', href: '/schoolAdmin/system-settings', icon: Cog6ToothIcon, current: false },
      { name: 'Security Settings', href: '/schoolAdmin/security-settings', icon: ShieldCheckIcon, current: false },
    ]
  }
];

// Teacher Navigation (Classroom and student management)
export const getTeacherNavigation = (): NavSection[] => [
  {
    title: 'Teaching Dashboard',
    items: [
      { name: 'Dashboard', href: '/teacher/dashboard', icon: HomeIcon, current: false },
      { name: 'Today\'s Schedule', href: '/teacher/schedule', icon: CalendarDaysIcon, current: false },
      { name: 'Live Session', href: '/teacher/session', icon: PlayIcon, current: false },
      { name: 'Quick Actions', href: '/teacher/quick', icon: SparklesIcon, current: false },
    ]
  },
  {
    title: 'My Classes',
    items: [
      // Removed routes that require admin API access:
      // { name: 'All Classes', href: '/teacher/classes', icon: BookOpenIcon, current: false },
      // { name: 'Class Attendance', href: '/teacher/attendance', icon: ClipboardDocumentCheckIcon, current: false },
      // { name: 'Student List', href: '/teacher/students', icon: UserIcon, current: false },
      { name: 'My Sessions', href: '/teacher/sessions', icon: PlayIcon, current: false },
    ]
  },
  {
    title: 'Academic Work',
    items: [
      { name: 'Lesson Plans', href: '/teacher/lessons', icon: DocumentTextIcon, current: false },
      // { name: 'Assignments', href: '/teacher/assignments', icon: ClipboardIcon, current: false },
      // { name: 'Grades & Marking', href: '/teacher/grades', icon: StarIcon, current: false },
      // { name: 'Assessments', href: '/teacher/assessments', icon: BeakerIcon, current: false },
      // { name: 'Subject Resources', href: '/teacher/resources', icon: FolderIcon, current: false },
    ]
  },
  {
    title: 'Teaching Tools',
    items: [
      // Note: These routes would need to be implemented with teacher-specific backends
      // { name: 'Student Profiles', href: '/teacher/profiles', icon: IdentificationIcon, current: false },
      // { name: 'RFID Scanner', href: '/teacher/rfid-scan', icon: QrCodeIcon, current: false },
      // { name: 'Attendance Tracker', href: '/teacher/attendance-track', icon: CheckCircleIcon, current: false },
    ]
  },
  {
    title: 'Communication',
    items: [
      // Note: Messages functionality would need teacher-specific backend implementation
      // { name: 'Messages', href: '/teacher/messages', icon: ChatBubbleBottomCenterTextIcon, current: false },
    ]
  },
  {
    title: 'Account',
    items: [
      // { name: 'My Profile', href: '/teacher/profile', icon: UserCircleIcon, current: false },
    ]
  }
];

// Parent Navigation (Child monitoring and school interaction)
export const getParentNavigation = (): NavSection[] => [
  {
    title: 'My Children',
    items: [
      { name: 'Dashboard', href: '/parent/dashboard', icon: HomeIcon, current: false },
      // { name: 'Child Overview', href: '/parent/children', icon: FaceSmileIcon, current: false },
      // { name: 'Live Location', href: '/parent/location', icon: MapPinIcon, current: false },
      // { name: 'Today\'s Activity', href: '/parent/activity', icon: ClockIcon, current: false },
    ]
  },
  {
    title: 'School Life',
    items: [
      { name: 'Attendance', href: '/parent/attendance', icon: CheckCircleIcon, current: false },
      // { name: 'Academic Progress', href: '/parent/academics', icon: ChartBarIcon, current: false },
      // { name: 'Report Cards', href: '/parent/reports', icon: DocumentTextIcon, current: false },
      // { name: 'Assignments', href: '/parent/assignments', icon: ClipboardIcon, current: false },
      // { name: 'Exam Results', href: '/parent/exams', icon: StarIcon, current: false },
      // { name: 'Behavioral Reports', href: '/parent/behavior', icon: HeartIcon, current: false },
    ]
  },
  {
    title: 'Transport Tracking',
    items: [
      { name: 'Bus Schedule', href: '/parent/transport/schedule', icon: CalendarDaysIcon, current: false },
      { name: 'Live Bus Tracking', href: '/parent/transport/live', icon: TruckIcon, current: false },
      { name: 'Pick-up/Drop-off', href: '/parent/transport/alerts', icon: BellIcon, current: false },
      { name: 'Route Information', href: '/parent/transport/routes', icon: MapPinIcon, current: false },
      { name: 'Driver Details', href: '/parent/transport/driver', icon: UserIcon, current: false },
    ]
  },
  {
    title: 'Communication',
    items: [
      { name: 'Teacher Messages', href: '/parent/messages/teachers', icon: AcademicCapIcon, current: false },
      { name: 'School Updates', href: '/parent/messages/school', icon: MegaphoneIcon, current: false },
      { name: 'Parent Groups', href: '/parent/messages/parents', icon: UserGroupIcon, current: false },
      // { name: 'Emergency Alerts', href: '/parent/messages/emergency', icon: ExclamationTriangleIcon, current: false },
    ]
  },
  {
    title: 'Wallet & Payments',
    items: [
      { name: 'My Wallet', href: '/parent/wallet', icon: WalletIcon, current: false },
      { name: 'Top Up My Wallet', href: '/parent/wallet/topup', icon: ArrowUpIcon, current: false },
      { name: 'Top Up Student Wallet', href: '/parent/wallet/student-topup', icon: UserPlusIcon, current: false },
      { name: 'Invoices', href: '/parent/wallet/invoices', icon: DocumentTextIcon, current: false },
      { name: 'Transactions', href: '/parent/wallet/transactions', icon: ArrowPathIcon, current: false },
    ]
  },
  {
    title: 'Account',
    items: [
      // { name: 'My Profile', href: '/parent/profile', icon: UserCircleIcon, current: false },
    ]
  },
  // {
  //   title: 'School Services',
  //   items: [
  //     { name: 'Events & Calendar', href: '/parent/events', icon: CalendarDaysIcon, current: false },
  //     { name: 'Cafeteria Menu', href: '/parent/menu', icon: CakeIcon, current: false },
  //     { name: 'Fee Payments', href: '/parent/payments', icon: CreditCardIcon, current: false },
  //     { name: 'Photo Gallery', href: '/parent/photos', icon: PhotoIcon, current: false },
  //     { name: 'School Directory', href: '/parent/directory', icon: UsersIcon, current: false },
  //   ]
  // },
  // {
  //   title: 'Settings',
  //   items: [
  //     { name: 'Notifications', href: '/parent/settings/notifications', icon: BellIcon, current: false },
  //     { name: 'Emergency Contacts', href: '/parent/settings/contacts', icon: PhoneIcon, current: false },
  //     { name: 'Privacy Settings', href: '/parent/settings/privacy', icon: LockClosedIcon, current: false },
  //     { name: 'App Settings', href: '/parent/settings/app', icon: Cog6ToothIcon, current: false },
  //   ]
  // }
];

// Driver Navigation (Transport and route management)
export const getDriverNavigation = (): NavSection[] => [
  {
    title: 'Driver Dashboard',
    items: [
      { name: 'Dashboard', href: '/driver/dashboard', icon: HomeIcon, current: false },
      { name: 'Today\'s Route', href: '/driver/route', icon: MapPinIcon, current: false },
      { name: 'Current Trip', href: '/driver/trip', icon: TruckIcon, current: false },
      { name: 'Quick Actions', href: '/driver/quick', icon: SparklesIcon, current: false },
    ]
  },
  {
    title: 'Route Management',
    items: [
      { name: 'My Routes', href: '/driver/routes', icon: MapPinIcon, current: false },
      { name: 'Route Schedule', href: '/driver/schedule', icon: CalendarDaysIcon, current: false },
      { name: 'Navigation', href: '/driver/navigation', icon: GlobeAltIcon, current: false },
      { name: 'Traffic Updates', href: '/driver/traffic', icon: SignalIcon, current: false },
    ]
  },
  {
    title: 'Student Management',
    items: [
      { name: 'My Students', href: '/driver/students', icon: UserIcon, current: false },
      { name: 'Attendance Check', href: '/driver/attendance', icon: ClipboardDocumentCheckIcon, current: false },
      { name: 'Student Pickup', href: '/driver/pickup', icon: UserPlusIcon, current: false },
      { name: 'Student Photos', href: '/driver/photos', icon: PhotoIcon, current: false },
      { name: 'Emergency Contacts', href: '/driver/emergency', icon: PhoneIcon, current: false },
    ]
  },
  {
    title: 'Safety & Compliance',
    items: [
      { name: 'Safety Checklist', href: '/driver/safety', icon: ShieldCheckIcon, current: false },
      { name: 'Vehicle Inspection', href: '/driver/inspection', icon: ClipboardDocumentListIcon, current: false },
      { name: 'Incident Reports', href: '/driver/incidents', icon: ExclamationTriangleIcon, current: false },
      { name: 'Emergency Procedures', href: '/driver/procedures', icon: LifebuoyIcon, current: false },
    ]
  },
  {
    title: 'Vehicle & Maintenance',
    items: [
      { name: 'Vehicle Status', href: '/driver/vehicle/status', icon: CubeIcon, current: false },
      { name: 'Fuel Tracking', href: '/driver/vehicle/fuel', icon: BeakerIcon, current: false },
      { name: 'Maintenance Log', href: '/driver/vehicle/maintenance', icon: WrenchScrewdriverIcon, current: false },
      { name: 'GPS Tracker', href: '/driver/vehicle/gps', icon: SignalIcon, current: false },
    ]
  },
  {
    title: 'Communication',
    items: [
      { name: 'School Contact', href: '/driver/messages/school', icon: BuildingOfficeIcon, current: false },
      { name: 'Parent Messages', href: '/driver/messages/parents', icon: UserGroupIcon, current: false },
      { name: 'Driver Chat', href: '/driver/messages/drivers', icon: TruckIcon, current: false },
      { name: 'Emergency Hotline', href: '/driver/messages/emergency', icon: ExclamationTriangleIcon, current: false },
    ]
  },
  {
    title: 'Account',
    items: [
      // { name: 'My Profile', href: '/driver/profile', icon: UserCircleIcon, current: false },
    ]
  }
];

// Student Navigation (Basic access for older students)
export const getStudentNavigation = (): NavSection[] => [
  {
    title: 'My School',
    items: [
      { name: 'Dashboard', href: '/student/dashboard', icon: HomeIcon, current: false },
      // { name: 'My Profile', href: '/student/profile', icon: UserIcon, current: false },
      { name: 'Today\'s Schedule', href: '/student/schedule', icon: CalendarDaysIcon, current: false },
      { name: 'School Map', href: '/student/map', icon: MapPinIcon, current: false },
    ]
  },
  {
    title: 'Academics',
    items: [
      { name: 'My Classes', href: '/student/classes', icon: BookOpenIcon, current: false },
      { name: 'Assignments', href: '/student/assignments', icon: ClipboardIcon, current: false },
      { name: 'My Grades', href: '/student/grades', icon: StarIcon, current: false },
      { name: 'Exam Schedule', href: '/student/exams', icon: BeakerIcon, current: false },
      { name: 'Study Resources', href: '/student/resources', icon: FolderIcon, current: false },
    ]
  },
  {
    title: 'School Life',
    items: [
      { name: 'My Attendance', href: '/student/attendance', icon: CheckCircleIcon, current: false },
      { name: 'School Events', href: '/student/events', icon: CakeIcon, current: false },
      { name: 'Clubs & Activities', href: '/student/clubs', icon: UsersIcon, current: false },
      { name: 'Library', href: '/student/library', icon: BookOpenIcon, current: false },
    ]
  },
  {
    title: 'My Wallet',
    items: [
      { name: 'Wallet Balance', href: '/student/wallet', icon: WalletIcon, current: false },
      { name: 'My Transactions', href: '/student/wallet/transactions', icon: ArrowPathIcon, current: false },
    ]
  },
  {
    title: 'Transport',
    items: [
      { name: 'Bus Schedule', href: '/student/transport/schedule', icon: CalendarDaysIcon, current: false },
      { name: 'My Bus Route', href: '/student/transport/route', icon: TruckIcon, current: false },
      { name: 'Bus Tracker', href: '/student/transport/tracker', icon: MapPinIcon, current: false },
    ]
  },
  {
    title: 'Services',
    items: [
      { name: 'Cafeteria Menu', href: '/student/cafeteria', icon: CakeIcon, current: false },
      { name: 'Photo Gallery', href: '/student/photos', icon: PhotoIcon, current: false },
      { name: 'School News', href: '/student/news', icon: NewspaperIcon, current: false },
      { name: 'Help & Support', href: '/student/support', icon: QuestionMarkCircleIcon, current: false },
    ]
  }
];

// Default Navigation (Fallback for unknown roles)
export const getDefaultNavigation = (): NavSection[] => [
  {
    title: 'General',
    items: [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, current: false },
      { name: 'Profile', href: '/profile', icon: UserIcon, current: false },
      { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, current: false },
      { name: 'Help', href: '/help', icon: QuestionMarkCircleIcon, current: false },
    ]
  }
];

// Helper function to get navigation based on user role
export const getNavigationByRole = (role: UserRole): NavSection[] => {
  switch (role) {
    case 'super_admin':
      return getSuperAdminNavigation();
    case 'school_admin':
      return getSchoolAdminNavigation();
    case 'teacher':
      return getTeacherNavigation();
    case 'parent':
      return getParentNavigation();
    case 'driver':
      return getDriverNavigation();
    case 'student':
      return getStudentNavigation();
    default:
      return getDefaultNavigation();
  }
};

// Helper function to get portal name based on role
export const getPortalNameByRole = (role: UserRole): string => {
  switch (role) {
    case 'super_admin':
      return 'Super Admin Portal';
    case 'school_admin':
      return 'School Admin Portal';
    case 'teacher':
      return 'Teacher Portal';
    case 'parent':
      return 'Parent Portal';
    case 'driver':
      return 'Driver Portal';
    case 'student':
      return 'Student Portal';
    default:
      return 'SmartTag Portal';
  }
};